import React, { useState, useRef } from "react";

const TreatmentsFilter = ({ onApplyFilters }) => {
    const [progress, setProgress] = useState(50);
    const [selectedTreatments, setSelectedTreatments] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [patientName, setPatientName] = useState("");
    const [doctorName, setDoctorName] = useState("");
    const [idSearch, setIdSearch] = useState("");

    const treatments = ["Cardiology", "Pediatrics", "Dermatology"];
    const statuses = ["Active", "Pending", "Completed", "Cancelled"];

    // Reference for offcanvas
    const offcanvasRef = useRef(null);

    const toggleSelection = (item, selectedList, setSelectedList) => {
        if (selectedList.includes(item)) {
            setSelectedList(selectedList.filter(i => i !== item));
        } else {
            setSelectedList([...selectedList, item]);
        }
    };

    const clearFilters = () => {
        setSelectedTreatments([]);
        setSelectedStatuses([]);
        setProgress(50);
        setPatientName("");
        setDoctorName("");
        setIdSearch("");

        // Limpiar filtros también en el componente padre
        onApplyFilters({
            selectedTreatments: [],
            selectedStatuses: [],
            progress: 50,
            patientName: "",
            doctorName: "",
            idSearch: ""
        });
    };

    const handleApplyFilters = () => {
        // Llamamos a la función onApplyFilters para enviar los filtros al componente padre
        onApplyFilters({
            selectedTreatments,
            selectedStatuses,
            progress,
            patientName,
            doctorName,
            idSearch
        });

        // Cierra el offcanvas utilizando la referencia
        if (offcanvasRef.current) {
            const offcanvasInstance = new window.bootstrap.Offcanvas(offcanvasRef.current);
            offcanvasInstance.hide();
        }
    };

    // Calcular filtros activos
    const activeFiltersCount = [
        ...selectedTreatments,
        ...selectedStatuses,
        patientName && patientName.trim(),
        doctorName && doctorName.trim(),
        idSearch && idSearch.trim(),
        progress !== 50 ? progress : null
    ].filter(Boolean).length;

    return (
        <div>
            <button
                className="btn btn-gray filterbtn d-flex align-items-center gap-1 position-relative"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#filterOffcanvas"
                aria-controls="filterOffcanvas"
            >
                <div className="filter_text d-flex align-items-center gap-1">
                    <span className="d-block material-symbols-outlined icon-md">tune</span>
                    <span className="d-block">Filter</span>
                </div>
                {/* Show badge only if there are active filters */}
                {activeFiltersCount > 0 && (
                    <span 
                        className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-primary" 
                        style={{fontSize: '0.6rem', padding: '0.2rem 0.4rem'}}
                    >
                        {activeFiltersCount}
                    </span>
                )}
            </button>

            {/* Offcanvas para filtros */}
            <div
                className="offcanvas offcanvas-end"
                tabIndex="-1"
                id="filterOffcanvas"
                aria-labelledby="filterOffcanvasLabel"
                style={{ width: '320px' }}
                ref={offcanvasRef}
            >
                {/* Header */}
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title" id="filterOffcanvasLabel">Filter</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                </div>

                {/* Body */}
                <div className="offcanvas-body overflow-auto" style={{ paddingBottom: '80px' }}>
                    {/* Search ID */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Search for the ID</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search ID"
                            value={idSearch}
                            onChange={(e) => setIdSearch(e.target.value)}
                        />
                    </div>

                    {/* Search Patient */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Search for Patient Name</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search for patient"
                            value={patientName}
                            onChange={(e) => setPatientName(e.target.value)}
                        />
                    </div>

                    {/* Treatments */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Treatment</label>
                        <div className="d-flex flex-wrap gap-2">
                            {treatments.map((treatment, index) => (
                                <button
                                    key={index}
                                    className={`btn btn-sm rounded-pill ${selectedTreatments.includes(treatment) ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => toggleSelection(treatment, selectedTreatments, setSelectedTreatments)}
                                >
                                    {treatment}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search Doctor */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Search for Doctor Name</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search for doctors"
                            value={doctorName}
                            onChange={(e) => setDoctorName(e.target.value)}
                        />
                    </div>

                    {/* Start Date */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Start Date</label>
                        <div className="d-flex gap-2">
                            <input type="date" className="form-control form-control-sm" />
                            <input type="date" className="form-control form-control-sm" />
                        </div>
                    </div>

                    {/* Next Session Date */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Next Session Date</label>
                        <div className="d-flex gap-2">
                            <input type="date" className="form-control form-control-sm" />
                            <input type="time" className="form-control form-control-sm" />
                        </div>
                    </div>

                    {/* Status */}
                    <div className="mb-3">
                        <label className="form-label small text-muted">Status</label>
                        <div className="d-flex flex-wrap gap-2">
                            {statuses.map((status, index) => (
                                <button
                                    key={index}
                                    className={`btn btn-sm rounded-pill ${selectedStatuses.includes(status) ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                    onClick={() => toggleSelection(status, selectedStatuses, setSelectedStatuses)}
                                >
                                    {status}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Progress */}
                    <div className="mb-4">
                        <label className="form-label small text-muted">Progress</label>
                        <div className="d-flex flex-column">
                            <input
                                type="range"
                                className="form-range"
                                min="0"
                                max="100"
                                value={progress}
                                onChange={(e) => setProgress(Number(e.target.value))}
                            />
                            <small className="text-muted">{progress}%</small>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div
                    className="position-absolute bottom-0 start-0 w-100 border-top bg-white p-3"
                    style={{ zIndex: 1 }}
                >
                    <div className="d-flex justify-content-between">
                        <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>Clear</button>
                        <button className="btn btn-primary btn-sm" data-bs-dismiss="offcanvas" onClick={handleApplyFilters}>Apply</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TreatmentsFilter;